import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Turnstile from '../components/Turnstile';
import './LoginPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

function getLandingPath(landingPage?: string): string {
  if (!landingPage || landingPage === 'collection') return '/';
  if (landingPage.startsWith('admin/')) return '/admin';
  return `/${landingPage}`;
}

export default function LoginPage() {
  const { login, user, prefs } = useAuth();
  const navigate = useNavigate();

  const [username,        setUsername]        = useState('');
  const [password,        setPassword]        = useState('');
  const [rememberMe,      setRememberMe]      = useState(true);
  const [error,           setError]           = useState('');
  const [loading,         setLoading]         = useState(false);
  const [turnstileToken,  setTurnstileToken]  = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  // Use a ref so the navigate effect always sees the latest prefs value
  const prefsRef = useRef(prefs);
  useEffect(() => { prefsRef.current = prefs; }, [prefs]);

  useEffect(() => {
    if (user) navigate(getLandingPath(prefsRef.current?.landingPage));
    // Check if registration is enabled (public endpoint)
    fetch(`${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api/config/registration-enabled`)
      .then(r => r.json())
      .then(d => setRegistrationEnabled(d.enabled === true))
      .catch(() => setRegistrationEnabled(false));
  }, [user]);

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!turnstileToken) {
      setError('Please wait for the security check to complete.');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, rememberMe, turnstileToken }),
      });
      const data = await res.json();
      if (res.ok) {
        // Store token and set user via AuthContext
        localStorage.setItem('guardianbazaar_token', data.token);
        // Fetch prefs before redirecting so landing page is applied
        try {
          const prefsRes = await fetch(`${API}/users/preferences`, {
            headers: { 'Authorization': `Bearer ${data.token}` }
          });
          if (prefsRes.ok) {
            const p = await prefsRes.json();
            window.location.href = getLandingPath(p.landingPage);
          } else {
            window.location.href = '/';
          }
        } catch {
          window.location.href = '/';
        }
      } else {
        setError(data.error || 'Login failed. Please check your credentials.');
        setPassword('');
        setTurnstileToken('');
      }
    } catch {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">

      {/* ── LEFT: Hero ── */}
      <div className="login-hero">
        <img
          src="/guardian-bazaar-logo-dark.png"
          alt="Guardian Bazaar"
          className="login-hero-logo"
        />
        <div className="login-hero-tagline">
          Your personal Magic: The Gathering<br/>card collection manager
        </div>
      </div>

      {/* ── RIGHT: Form ── */}
      <div className="login-form-panel">
        <div className="login-greeting">
          <h1 className="login-title">Sign in to your collection</h1>
        </div>

        <form className="login-form" onSubmit={handleLogin}>
          <div className="login-field">
            <label>Username or Email</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter username or email"
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className="login-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </div>
          <div className="login-remember">
            <label className="login-check-label">
              <input type="checkbox" checked={rememberMe}
                     onChange={e => setRememberMe(e.target.checked)} />
              Remember me for 30 days
            </label>
          </div>
          {error && <div className="login-error">{error}</div>}
          <Turnstile
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken('')}
            onError={() => setTurnstileToken('')}
          />
          <button type="submit" className="login-btn" disabled={loading || !turnstileToken}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
          <div className="login-links-below">
            <a href="/forgot-password" className="login-link">Forgot password?</a>
            {registrationEnabled && (
              <div className="login-register-prompt">
                Don't have an account? <a href="/register" className="login-link-accent">Create account</a>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
